import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import {
  KpiReportFilter,
  ReportRequestParams,
} from 'src/app/common/dtos/reports.dto';
import { translateKpiReportFilterFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { ReportFiltersDTO } from 'src/app/private/pages/reporting-page/filters/report-filters.dto';
import { ChartType } from '../../../enums/chart-type.enum';
import { AlertService } from '../../../services/alert/alert.service';
import { FetchLastUpdatedDate } from '../../../state/reporting/reporting.actions';
import { ReportingState } from '../../../state/reporting/reporting.state';
import { ProFormsAPIResponse } from '../../../types/responses/proforms.responses';
import { ChartColorsAll } from '../../reports/report-controller/series-helper';

@Component({
  selector: 'app-proforms-cumulative-quiz',
  standalone: false,

  templateUrl: './proforms-cumulative-quiz.component.html',
  styleUrl: './proforms-cumulative-quiz.component.scss',
})
export class ProformsCumulativeQuizComponent implements OnInit, OnDestroy {
  @Input() proFormId: number;

  @Input() sessionId: number;

  subs: Subscription[] = [];

  loading = true;

  chartColors = ChartColorsAll;

  chartTypes = ChartType;

  reportFilters: ReportFiltersDTO;

  filters: KpiReportFilter[];

  selectedProForm: ProFormsAPIResponse | null = null;

  questionTitles: string[];

  optionTypes: string[];

  observedAnswersParams: ReportRequestParams[];

  textQuestionIds: string[];

  textQuestionTitles: string[];

  textQuestionTypes: string[];

  textAnswersParams: ReportRequestParams[];

  quizSummaryParams: ReportRequestParams;

  questionsOrder: string[];

  lastUpdatedDate: string;

  constructor(
    private store: Store,
    private apiService: APICoreService,
    private alertService: AlertService
  ) {
    const updatedDate$ = this.store.select(ReportingState.getUpdatedDate);
    this.subs.push(
      updatedDate$.subscribe((date) => {
        this.lastUpdatedDate = date;
      })
    );
  }

  ngOnInit() {
    if (this.proFormId) {
      this.apiService
        .getRequest(
          `growelab/pro-forms/forms/${this.proFormId}?expand=district,districtsSharedWith.district,districtsExcludedFrom.district,submissions,sections.questions.answerOptions`
        )
        .subscribe({
          error: (error) => {
            this.alertService.showAlert(`${error.error.message}`);
          },
          next: (response) => {
            if (response.item) {
              this.loading = false;
              this.selectedProForm = response.item;
              this.reportFilters = {
                reportName: 'proforms_reports',
                proFormFilter: response.item,
              };
              this.loadReports();
            }
          },
        });
    }
  }

  loadReports() {
    this.filters = translateKpiReportFilterFromFilterDTO(this.reportFilters);
    this.getQuestionOrder();
    this.store.dispatch(new FetchLastUpdatedDate());
    this.getQuestionOptions();
  }

  getQuestionOptions() {
    this.observedAnswersParams = [];
    this.questionTitles = [];
    this.optionTypes = [];
    this.textQuestionIds = [];
    this.textQuestionTitles = [];
    this.textQuestionTypes = [];
    this.textAnswersParams = [];

    if (this.selectedProForm) {
      this.getQuizSummary();
      this.selectedProForm.sections?.forEach((section) => {
        section.questions?.forEach((question) => {
          if (
            question.field_type === 'checkbox' ||
            question.field_type === 'radio' ||
            question.field_type === 'scale'
          ) {
            this.questionTitles.push(question.question);
            this.optionTypes.push(question.field_type);
            this.observedAnswersParams.push({
              dimension: 'proform_question_answer_option_name',
              dataset: 'fct_proform_submission_answer',
              measure: {
                type: 'count',
                field: '*',
              },
              filters: [
                ...this.filters,
                {
                  codename: 'proform_form_id',
                  value: [question.proform_form_id.toString()],
                },
                {
                  codename: 'proform_question_id',
                  value: [question.id.toString()],
                },
                {
                  codename: 'context_table',
                  value: ['pd_session'],
                },
                {
                  codename: 'context_table_id',
                  value: [this.sessionId.toString()],
                },
              ],
            });
          } else if (
            question.field_type === 'text' ||
            question.field_type === 'textarea' ||
            question.field_type === 'attachment'
          ) {
            this.textQuestionTitles.push(question.question || 'Text Question');
            this.textQuestionTypes.push(question.field_type);
            this.textQuestionIds.push(question.id.toString());
            this.textAnswersParams.push({
              dataset: 'fct_proform_submission_answer',
              measure: {
                type: 'count',
                field: '*',
              },
              filters: [
                ...this.filters,
                {
                  codename: 'proform_form_id',
                  value: [question.proform_form_id.toString()],
                },
                {
                  codename: 'proform_question_id',
                  value: [question.id.toString()],
                },
                {
                  codename: 'context_table',
                  value: ['pd_session'],
                },
                {
                  codename: 'context_table_id',
                  value: [this.sessionId.toString()],
                },
              ],
              page: 0,
              per_page: 10,
            });
          }
        });
      });
    }
  }

  getQuizSummary() {
    if (this.selectedProForm) {
      this.quizSummaryParams = {
        id: 'proform-question-quiz',
        dimension: 'grade',
        dataset: 'fct_proform_submission',
        measure: {
          type: 'count',
          field: '*',
        },
        filters: [
          ...this.filters,
          {
            codename: 'proform_form_id',
            value: [this.selectedProForm.id.toString()],
          },
          {
            codename: 'context_table',
            value: ['pd_session'],
          },
          {
            codename: 'context_table_id',
            value: [this.sessionId.toString()],
          },
        ],
      };
    }
  }

  getQuestionOrder() {
    this.questionsOrder = [];
    this.selectedProForm?.sections?.[0]?.questions?.forEach((question) => {
      this.questionsOrder.push(question.id.toString());
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getQuestionId(params: ReportRequestParams) {
    return params.filters.find(
      (filter) => filter.codename === 'proform_question_id'
    )?.value[0];
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
